import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import s from "./WorkOrders.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useWorkOrders } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  ageDays,
  isOverdue,
  KIND_LABEL,
  STATUS_LABEL,
  WORK_ORDER_KINDS,
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_STATUSES,
  type WorkOrder,
  type WorkOrderPriority,
} from "../../domain/workOrders";
import {
  DATE_FIELD_LABEL,
  DATE_FIELDS,
  disciplineLabel,
  isoDay,
  matches,
  presetRange,
  RANGE_PRESET_LABEL,
  RANGE_PRESETS,
  repairTypeOf,
  SLA_LABEL,
  SLA_STATES,
  slaRemaining,
  slaState,
  SORT_LABEL,
  SORTS,
  sortOrders,
  UNCLASSIFIED,
  workOrdersCsv,
  workOrderStats,
  type Scope,
} from "../../domain/workOrderReport";
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  numClass,
  PriorityBadge,
  Segmented,
  Select,
  Skeleton,
  StatusBadge,
  Table,
  tableRowLinkClass,
} from "../../design/components";
import { formatDate } from "../../lib/format";
import { t } from "../../i18n";
import { CreateWorkOrderDialog } from "./CreateWorkOrderDialog";
import { DeliveryMark } from "./WorkOrderDelivery";
import { deliveryOf } from "../../domain/workOrderDelivery";
import { useWorkOrderFilters } from "./useWorkOrderFilters";
import { WorkOrderStats, type Dimension } from "./WorkOrderStats";
import { WorkOrderFlow } from "./WorkOrderFlow";
import { SLA_TONE } from "./slaTone";
import { saveFile } from "../../lib/saveFile";
import {
  AREA_HINT,
  AREA_LABEL,
  AREAS,
  areaKey,
  areaOf,
  contractorChoices,
  contractorOf,
  NOT_SET,
  OWN_TEAM,
  TRADE_LABEL,
  TRADES,
  tradeOf,
} from "../../domain/workOrderClass";

/** The set is known only after the orders load, so the choices come from the data. */
function distinct(values: string[]): string[] {
  const set = [...new Set(values)].sort((a, b) => a.localeCompare(b));
  // "Not classified" is a real answer, listed last rather than alphabetically.
  return set.includes(UNCLASSIFIED) ? [...set.filter((v) => v !== UNCLASSIFIED), UNCLASSIFIED] : set;
}

/**
 * The app's CSV and text exports, all of them, through one door.
 *
 * Kept as a plain call rather than made to return a promise: a dozen callers
 * fire this from a click handler and have nothing useful to do with the
 * outcome. `saveFile` tells the person itself when a save cannot happen.
 */
export function downloadText(filename: string, text: string, type: string) {
  // A byte-order mark, or Excel opens UTF-8 as Windows-1252 and "—" arrives as "â€”".
  void saveFile(filename, new Blob(["\uFEFF", text], { type }));
}

export function WorkOrdersPage() {
  const { site, serverTime, principal } = useSession();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [noRole, setNoRole] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const { filters: f, params, set, setMany, clear } = useWorkOrderFilters();

  /* One status is filtered by the platform; everything else here. */
  const [waiting, setWaiting] = useState(false);
  /* while an order is on its way to a contractor, look again every 15 s so its color changes on its own */
  const orders = useWorkOrders(f.status ? { status: f.status } : {}, { refetchMs: waiting ? 15_000 : false });
  const now = useMemo(() => new Date(serverTime), [serverTime]);
  const all = useMemo(() => orders.data?.items ?? [], [orders.data]);
  /* the process strip counts every order, whatever the list is filtered to */
  const everything = useWorkOrders({});
  const anyWaiting = (orders.data?.items ?? []).some((w) => {
    const d = deliveryOf(w);
    return d === "sending" || d === "delivered";
  });
  if (anyWaiting !== waiting) setWaiting(anyWaiting);
  const atStep = useMemo(() => {
    const n: Partial<Record<WorkOrder["status"], number>> = {};
    for (const w of everything.data?.items ?? []) n[w.status] = (n[w.status] ?? 0) + 1;
    return n;
  }, [everything.data]);

  const rows = useMemo(
    () =>
      sortOrders(
        all.filter((w) => matches(w, f, now)),
        f.sort,
        now,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, params.toString(), now],
  );
  const stats = useMemo(() => workOrderStats(rows, now), [rows, now]);
  const repairTypes = useMemo(() => distinct(all.map(repairTypeOf)), [all]);
  const disciplines = useMemo(() => distinct(all.map((w) => disciplineLabel(w.discipline))), [all]);
  const contractors = useMemo(() => contractorChoices([], all), [all]);

  const canWrite = hasPermission(principal, PERMISSION.workOrdersWrite);
  const filtered = params.toString() !== "";

  const pick = (dimension: Dimension, key: string) => {
    const current = {
      status: f.status,
      priority: f.priority ? String(f.priority) : null,
      kind: f.kind,
      repair: f.repairType,
      discipline: f.discipline,
      sla: f.sla,
      area: f.area ?? null,
      trade: f.trade ?? null,
      contractor: f.contractor ?? null,
    }[dimension];
    const next = current === key ? null : key;
    // A status needs the scope that contains it, or the row would filter to nothing.
    setMany(dimension === "status" ? { status: next, scope: next ? "all" : null } : { [dimension]: next });
  };

  /* Filters used less often sit behind "More filters"; it opens by itself when one of them is set. */
  const hiddenSet = [
    f.status,
    f.kind,
    f.repairType,
    f.discipline,
    f.sla,
    f.overdueOnly ? "1" : null,
    f.from,
    f.to,
    f.sort !== "priority" ? f.sort : null,
  ].filter(Boolean).length;
  const [moreOpen, setMoreOpen] = useState(hiddenSet > 0);

  /* Area tab counts: every other filter applied, the area itself not. */
  const areaCounts = useMemo(() => {
    const n: Record<string, number> = { all: 0 };
    for (const w of all) {
      if (!matches(w, { ...f, area: null }, now)) continue;
      n.all += 1;
      n[areaKey(w)] = (n[areaKey(w)] ?? 0) + 1;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, params.toString(), now]);

  return (
    <Page
      eyebrow={t("{code} · maintenance", { code: site.code })}
      title="Work orders"
      actions={
        /* Never a dead button: without the right role it says which role is needed and where to get it. */
        <Button variant="primary" onClick={() => (canWrite ? setCreating(true) : setNoRole(true))}>
          {t("+ New Work Order")}
        </Button>
      }
    >
      <WorkOrderFlow counts={atStep} active={f.status} onPick={(st) => pick("status", st)} />

      <div className={s.areaBar}>
        <div className={s.areaTabs} role="tablist" aria-label={t("Area")}>
          {(["all", ...AREAS] as const).map((a) => {
            const on = a === "all" ? !f.area : f.area === a;
            return (
              <button
                key={a}
                type="button"
                role="tab"
                aria-selected={on}
                className={on ? `${s.areaTab} ${s.areaTabOn}` : s.areaTab}
                title={a === "all" ? undefined : t(AREA_HINT[a])}
                onClick={() => set("area", a === "all" ? null : a)}
              >
                {a === "all" ? t("All areas") : t(AREA_LABEL[a])}
                <span className={s.areaCount}>{areaCounts[a] ?? 0}</span>
              </button>
            );
          })}
          {(areaCounts[NOT_SET] ?? 0) > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={f.area === NOT_SET}
              className={f.area === NOT_SET ? `${s.areaTab} ${s.areaTabOn}` : s.areaTab}
              onClick={() => set("area", f.area === NOT_SET ? null : NOT_SET)}
            >
              {t("Not set")}
              <span className={s.areaCount}>{areaCounts[NOT_SET]}</span>
            </button>
          )}
        </div>
        <div className={s.toolbar}>
          <Button size="sm" onClick={() => setShowStats((v) => !v)} aria-expanded={showStats}>
            {showStats ? t("Hide statistics") : t("Statistics")}
          </Button>
          <Button
            size="sm"
            onClick={() => navigate(`/work-orders/report${params.toString() ? `?${params}` : ""}`)}
            disabled={!orders.data}
          >
            {t("Report")}
          </Button>
          <Button
            size="sm"
            onClick={() =>
              downloadText(`work-orders-${site.code}-${isoDay(now)}.csv`, workOrdersCsv(rows), "text/csv;charset=utf-8")
            }
            disabled={!rows.length}
          >
            {t("Export CSV")}
          </Button>
        </div>
      </div>

      <div className={s.filters}>
        <Segmented<Scope>
          ariaLabel="Scope"
          value={f.scope}
          options={[
            { value: "open", label: "Open" },
            { value: "closed", label: "Closed" },
            { value: "all", label: "All" },
          ]}
          onChange={(v) => setMany({ scope: v === "open" ? null : v, ...(v !== "all" ? { status: null } : {}) })}
        />
        <Select value={f.trade ?? ""} onChange={(e) => set("trade", e.target.value || null)} aria-label={t("Trade")}>
          <option value="">{t("All trades")}</option>
          {TRADES.map((k) => (
            <option key={k} value={k}>
              {t(TRADE_LABEL[k])}
            </option>
          ))}
          <option value={NOT_SET}>{t("Trade not set")}</option>
        </Select>
        <Select
          value={f.contractor ?? ""}
          onChange={(e) => set("contractor", e.target.value || null)}
          aria-label={t("Contractor")}
        >
          <option value="">{t("All contractors")}</option>
          {contractors.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          <option value={OWN_TEAM}>{t("Own team")}</option>
        </Select>
        <Select
          value={f.priority ?? ""}
          onChange={(e) => set("priority", e.target.value || null)}
          aria-label={t("Priority")}
        >
          <option value="">{t("Any priority")}</option>
          {WORK_ORDER_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              P{p}
            </option>
          ))}
        </Select>
        <Input
          type="search"
          placeholder={t("Search number, title, asset, contractor…")}
          value={f.q}
          onChange={(e) => set("q", e.target.value || null)}
          aria-label={t("Search")}
        />
        <Button size="sm" variant="ghost" onClick={() => setMoreOpen((v) => !v)} aria-expanded={moreOpen}>
          {moreOpen ? t("Fewer filters") : t("More filters")}
          {hiddenSet > 0 && !moreOpen ? ` (${hiddenSet})` : ""}
        </Button>
        {filtered && (
          <Button size="sm" variant="ghost" onClick={clear}>
            {t("Reset")}
          </Button>
        )}
        <span className={s.count}>{orders.data ? t("{n} of {total}", { n: rows.length, total: all.length }) : ""}</span>
      </div>

      {moreOpen && (
        <div className={s.moreFilters} data-testid="wo-more-filters">
          <div className={s.filters}>
            <Select
              value={f.status ?? ""}
              onChange={(e) => set("status", e.target.value || null)}
              aria-label={t("Status")}
            >
              <option value="">{t("Any status")}</option>
              {WORK_ORDER_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(STATUS_LABEL[st])}
                </option>
              ))}
            </Select>
            <Select value={f.kind ?? ""} onChange={(e) => set("kind", e.target.value || null)} aria-label={t("Kind")}>
              <option value="">{t("Any kind")}</option>
              {WORK_ORDER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(KIND_LABEL[k])}
                </option>
              ))}
            </Select>
            <Select
              value={f.repairType ?? ""}
              onChange={(e) => set("repair", e.target.value || null)}
              aria-label={t("Repair type")}
            >
              <option value="">{t("Any repair type")}</option>
              {repairTypes.map((r) => (
                <option key={r} value={r}>
                  {r === UNCLASSIFIED ? t("Not classified") : r}
                </option>
              ))}
            </Select>
            <Select
              value={f.discipline ?? ""}
              onChange={(e) => set("discipline", e.target.value || null)}
              aria-label={t("Discipline")}
            >
              <option value="">{t("Any discipline")}</option>
              {disciplines.map((d) => (
                <option key={d} value={d}>
                  {d === UNCLASSIFIED ? t("Not classified") : d}
                </option>
              ))}
            </Select>
            <Select
              value={f.sla ?? ""}
              onChange={(e) => set("sla", e.target.value || null)}
              aria-label={t("SLA status")}
            >
              <option value="">{t("Any SLA status")}</option>
              {SLA_STATES.map((k) => (
                <option key={k} value={k}>
                  {t(SLA_LABEL[k])}
                </option>
              ))}
            </Select>
            <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13.5 }}>
              <input
                type="checkbox"
                checked={f.overdueOnly}
                onChange={(e) => set("overdue", e.target.checked ? "1" : null)}
              />
              {t("Overdue only")}
            </label>
          </div>
          <div className={s.filterRow2}>
            <span className={s.rangeLabel}>{t("DATE RANGE")}</span>
            <Select
              value={f.dateField}
              onChange={(e) => set("date", e.target.value === "opened" ? null : e.target.value)}
              aria-label={t("Date to filter on")}
            >
              {DATE_FIELDS.map((d) => (
                <option key={d} value={d}>
                  {t(DATE_FIELD_LABEL[d])}
                </option>
              ))}
            </Select>
            <Input
              type="date"
              value={f.from ?? ""}
              max={f.to}
              onChange={(e) => set("from", e.target.value || null)}
              aria-label={t("From")}
            />
            <span className={s.rangeLabel}>{t("TO")}</span>
            <Input
              type="date"
              value={f.to ?? ""}
              min={f.from}
              onChange={(e) => set("to", e.target.value || null)}
              aria-label={t("To")}
            />
            <span className={s.presets}>
              {RANGE_PRESETS.map((p) => (
                <Button key={p} size="sm" onClick={() => setMany(presetRange(p, now))}>
                  {t(RANGE_PRESET_LABEL[p])}
                </Button>
              ))}
              {(f.from || f.to) && (
                <Button size="sm" variant="ghost" onClick={() => setMany({ from: null, to: null })}>
                  {t("Clear dates")}
                </Button>
              )}
            </span>
            <span className={s.rangeLabel}>{t("SORT")}</span>
            <Select
              value={f.sort}
              onChange={(e) => set("sort", e.target.value === "priority" ? null : e.target.value)}
              aria-label={t("Sort")}
            >
              {SORTS.map((k) => (
                <option key={k} value={k}>
                  {t(SORT_LABEL[k])}
                </option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {orders.data?.truncated && (
        <div className={s.truncated} role="status">
          {orders.data.note ?? t("The platform returned only the first page of work orders. Narrow with a status.")}
        </div>
      )}

      {showStats && orders.data && (
        <WorkOrderStats
          stats={stats}
          active={{
            status: f.status,
            priority: f.priority ? String(f.priority) : null,
            kind: f.kind,
            repair: f.repairType,
            discipline: f.discipline,
            sla: f.sla,
            area: f.area ?? null,
            trade: f.trade ?? null,
            contractor: f.contractor ?? null,
          }}
          onPick={pick}
        />
      )}

      {orders.isPending ? (
        <Skeleton rows={8} height={18} />
      ) : orders.isError ? (
        <ErrorState error={orders.error} retry={() => void orders.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState>
          {t("No work orders match.")}{" "}
          {all.length === 0 && t("The platform lists none at {code}.", { code: site.code })}
        </EmptyState>
      ) : (
        <WorkOrderTable rows={rows} now={now} onOpen={(w) => navigate(`/work-orders/${w.id}`)} />
      )}

      {noRole && (
        <div
          className={s.noRole}
          role="alertdialog"
          aria-label={t("You can't create work orders yet")}
          data-testid="wo-no-role"
        >
          <b>{t("You can't create work orders yet")}</b>
          <p>
            {t(
              "Your account can view work orders but can't create them. Ask an administrator to add the Supervisor or Technician role to your account in Administration › People.",
            )}
          </p>
          <div className={s.noRoleActions}>
            <Link to="/administration">{t("Open Administration")}</Link>
            <Button size="sm" onClick={() => setNoRole(false)}>
              {t("Close")}
            </Button>
          </div>
        </div>
      )}

      {creating && (
        <CreateWorkOrderDialog
          onClose={() => setCreating(false)}
          onCreated={(wo) => navigate(`/work-orders/${wo.id}`)}
        />
      )}
    </Page>
  );
}

export function WorkOrderTable({
  rows,
  now,
  onOpen,
  full = false,
}: {
  rows: WorkOrder[];
  now: Date;
  /** Omitted in the printed report, where a row is not a link. */
  onOpen?: (w: WorkOrder) => void;
  /** Every column, one fact each — the printed report. The list folds them into seven. */
  full?: boolean;
}) {
  if (full)
    return (
      <Table>
        <thead>
          <tr>
            <th>{t("Number")}</th>
            <th>{t("Pri")}</th>
            <th>{t("Work")}</th>
            <th>{t("Area")}</th>
            <th>{t("Trade")}</th>
            <th>{t("Contractor")}</th>
            <th>{t("Repair type")}</th>
            <th>{t("Kind")}</th>
            <th>{t("Status")}</th>
            <th>{t("SLA")}</th>
            <th>{t("Zone")}</th>
            <th>{t("Crew")}</th>
            <th>{t("Opened")}</th>
            <th>{t("Completed")}</th>
            <th style={{ textAlign: "end" }}>{t("Age")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <FullRow key={w.id} wo={w} now={now} />
          ))}
        </tbody>
      </Table>
    );
  return (
    <Table>
      <thead>
        <tr>
          <th>{t("Number")}</th>
          <th>{t("Pri")}</th>
          <th>{t("Work")}</th>
          <th>{t("Trade")}</th>
          <th>{t("Contractor")}</th>
          <th>{t("Status")}</th>
          <th>{t("Opened")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((w) => (
          <Row key={w.id} wo={w} now={now} onOpen={onOpen ? () => onOpen(w) : undefined} />
        ))}
      </tbody>
    </Table>
  );
}

function tradeText(wo: WorkOrder): string | null {
  const tr = tradeOf(wo).value;
  return tr ? t(TRADE_LABEL[tr]) : null;
}
function areaText(wo: WorkOrder): string | null {
  const a = areaOf(wo).value;
  return a ? t(AREA_LABEL[a]) : null;
}

function SlaCell({ wo, now }: { wo: WorkOrder; now: Date }) {
  if (wo.slaHours == null) return <span className="muted">—</span>;
  return (
    <>
      <span style={{ color: SLA_TONE[slaState(wo, now)], fontWeight: 600 }}>{t(SLA_LABEL[slaState(wo, now)])}</span>
      <div className={s.titleSub}>
        {wo.slaHours} h{slaRemaining(wo, now) ? ` · ${slaRemaining(wo, now)}` : ""}
      </div>
    </>
  );
}

/** One row of the list: seven columns, the second line of each carrying what the old columns showed. */
function Row({ wo, now, onOpen }: { wo: WorkOrder; now: Date; onOpen?: () => void }) {
  const age = ageDays(wo, now);
  const overdue = isOverdue(wo, now);
  const where = [wo.assetTag, wo.zoneCode && wo.zoneCode !== wo.assetTag ? wo.zoneCode : null, t(KIND_LABEL[wo.kind])]
    .filter(Boolean)
    .join(" · ");
  const sla = wo.slaHours == null ? null : slaState(wo, now);
  return (
    <tr
      className={onOpen ? tableRowLinkClass : undefined}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (onOpen && e.key === "Enter") onOpen();
      }}
      tabIndex={onOpen ? 0 : undefined}
    >
      <td className={s.idCell}>
        <Link to={`/work-orders/${wo.id}`} onClick={(e) => e.stopPropagation()}>
          {wo.number}
        </Link>
      </td>
      <td>
        <PriorityBadge priority={wo.priority as WorkOrderPriority} />
      </td>
      <td className={s.titleCell}>
        <div>{wo.title}</div>
        <div className={s.titleSub}>{where}</div>
      </td>
      <td>
        {tradeText(wo) ?? <span className="muted">{t("Not set")}</span>}
        <div className={s.titleSub}>{areaText(wo) ?? ""}</div>
      </td>
      <td>
        {contractorOf(wo) ?? <span className="muted">{t("Own team")}</span>}
        {wo.crew && <div className={s.titleSub}>{wo.crew}</div>}
      </td>
      <td>
        <StatusBadge status={wo.status} />
        {overdue && <span className={s.overdue}>{t("OVERDUE")}</span>}
        <DeliveryMark wo={wo} />
        {sla && sla !== "none" && sla !== "void" && (
          <div className={s.titleSub} style={{ color: SLA_TONE[sla] }}>
            {t("SLA {state}", { state: t(SLA_LABEL[sla]).toLowerCase() })}
          </div>
        )}
      </td>
      <td>
        {formatDate(wo.openedAt)}
        <div className={s.titleSub}>{t("{n}d old", { n: age })}</div>
      </td>
    </tr>
  );
}

function FullRow({ wo, now }: { wo: WorkOrder; now: Date }) {
  const overdue = isOverdue(wo, now);
  return (
    <tr>
      <td className={s.idCell}>{wo.number}</td>
      <td>
        <PriorityBadge priority={wo.priority as WorkOrderPriority} />
      </td>
      <td className={s.titleCell}>
        <div>{wo.title}</div>
        {wo.assetTag && <div className={s.titleSub}>{wo.assetTag}</div>}
      </td>
      <td>{areaText(wo) ?? <span className="muted">—</span>}</td>
      <td>{tradeText(wo) ?? <span className="muted">—</span>}</td>
      <td>{contractorOf(wo) ?? <span className="muted">{t("Own team")}</span>}</td>
      <td>
        {repairTypeOf(wo) === UNCLASSIFIED ? <span className="muted">{t("Not classified")}</span> : repairTypeOf(wo)}
        {wo.discipline && <div className={s.titleSub}>{disciplineLabel(wo.discipline)}</div>}
      </td>
      <td>{t(KIND_LABEL[wo.kind])}</td>
      <td>
        <StatusBadge status={wo.status} />
        {overdue && <span className={s.overdue}>{t("OVERDUE")}</span>}
      </td>
      <td>
        <SlaCell wo={wo} now={now} />
      </td>
      <td className="mono">{wo.zoneCode ?? "—"}</td>
      <td>{wo.crew ?? <span className="muted">{t("unassigned")}</span>}</td>
      <td>{formatDate(wo.openedAt)}</td>
      <td>{formatDate(wo.completedAt)}</td>
      <td className={numClass}>{t("{n}d", { n: ageDays(wo, now) })}</td>
    </tr>
  );
}
